import type { z } from 'zod'

/**
 * The browser's door to the API.
 *
 * **It parses every response rather than asserting its shape.** A client typed off the server
 * and trusting what arrives is correct exactly until the two are deployed out of step — and
 * the SPAs are static files on CloudFront, so being out of step with the API is the normal
 * state for minutes at a time after every release. A parse turns that into one legible error
 * at the boundary instead of `undefined is not an object` three components deep.
 *
 * > **Spike 0.6 is the intended replacement for the typing half of this**, not for the
 * > parsing half. `packages/api-client` is meant to carry a built `.d.ts` holding the resolved
 * > Hono RPC `Client` type, so route paths and request bodies are checked at compile time. That
 * > pipeline is not built yet; until it is, each feature owns the schema for the endpoint it
 * > calls and this module owns the transport. Response validation stays either way.
 *
 * Errors are the platform's envelope: a stable `code`, a human `message`, and machine-readable
 * `detail`. They are thrown as a typed `ApiClientError` so a caller can branch on the code —
 * `feature_read_only` renders a different screen from `forbidden`, and the whole point of the
 * refusal vocabulary is that the client never has to string-match prose.
 */

export type ApiErrorBody = {
  code: string
  message: string
  detail?: Record<string, unknown>
}

export class ApiClientError extends Error {
  readonly status: number
  readonly code: string
  readonly detail: Record<string, unknown> | undefined

  constructor(status: number, body: ApiErrorBody) {
    super(body.message)
    this.name = 'ApiClientError'
    this.status = status
    this.code = body.code
    this.detail = body.detail
  }

  /**
   * The module is not on this tenant's plan. Its routes 404 by design (§3.4).
   *
   * **`detail.module` is the discriminator, and without it this matched every 404.** The API
   * answers `not_found` to three different things — a module the tenant does not hold, a tenant
   * that could not be resolved, and a route that does not exist — because a module a tenant has
   * not bought must look absent rather than withheld. Only the first carries
   * `detail: { module }`, set by `moduleNotFound()`.
   *
   * Matching on status and code alone made every one of those read as "not part of this plan".
   * A signed-out visitor to the console saw it, because the session probe 404s before anything
   * knows who they are — a plan error shown to somebody who has simply not logged in yet.
   */
  get isMissingFeature(): boolean {
    return this.status === 404 && this.code === 'not_found' && this.detail?.['module'] !== undefined
  }

  /** The plan lapsed: reads work, writes do not. A different screen from "you lack permission". */
  get isReadOnly(): boolean {
    return this.code === 'feature_read_only'
  }
}

/**
 * The transport, as the **call signature** this client actually uses.
 *
 * Not `typeof globalThis.fetch`: that type carries whatever the host runtime bolts on — Bun
 * adds `preconnect` — so a plain function would not satisfy it, and every test would need a
 * stub pretending to be a whole runtime. A port should name what the caller invokes, not what
 * the ambient global happens to be.
 */
export type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>

export type ApiClientOptions = {
  /** Absolute in development (the API is on another port); same-origin in production. */
  baseUrl: string
  /**
   * Swapped in tests. Not defaulted to a bound `globalThis.fetch` at module scope, because
   * that captures whatever `fetch` existed at import time and makes mocking order-dependent.
   */
  fetch?: FetchLike
}

export type RequestOptions<T> = {
  /** The schema the response must satisfy. Parsing, not asserting — see above. */
  schema: z.ZodType<T>
  body?: unknown
  signal?: AbortSignal
}

export type ApiClient = {
  get: <T>(path: string, options: RequestOptions<T>) => Promise<T>
  post: <T>(path: string, options: RequestOptions<T>) => Promise<T>
  put: <T>(path: string, options: RequestOptions<T>) => Promise<T>
  patch: <T>(path: string, options: RequestOptions<T>) => Promise<T>
  delete: <T>(path: string, options: RequestOptions<T>) => Promise<T>
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const doFetch = options.fetch ?? ((input, init) => globalThis.fetch(input, init))

  async function request<T>(
    method: string,
    path: string,
    { schema, body, signal }: RequestOptions<T>,
  ): Promise<T> {
    const response = await doFetch(`${options.baseUrl}${path}`, {
      method,
      headers: body === undefined ? {} : { 'content-type': 'application/json' },
      // The session is a cookie, and the SPA is on a different origin in development. Without
      // this the browser sends no cookie and every request reads as signed out.
      credentials: 'include',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal === undefined ? {} : { signal }),
    })

    if (!response.ok) throw await toError(response)

    // 204 and friends. Parsing `undefined` lets a schema of `z.void()` or `z.null()` decide
    // what an empty body means, rather than this function guessing.
    const text = await response.text()
    const payload: unknown = text === '' ? undefined : JSON.parse(text)

    const parsed = schema.safeParse(payload)
    if (!parsed.success) {
      // Named loudly. A response that does not match is a deploy skew or a contract change,
      // and both are worth an error somebody can search for.
      throw new ApiClientError(response.status, {
        code: 'response_shape',
        message: `The API's response to ${method} ${path} did not match what this client expects.`,
        detail: { issues: parsed.error.issues },
      })
    }

    return parsed.data
  }

  return {
    get: (path, opts) => request('GET', path, opts),
    post: (path, opts) => request('POST', path, opts),
    put: (path, opts) => request('PUT', path, opts),
    patch: (path, opts) => request('PATCH', path, opts),
    delete: (path, opts) => request('DELETE', path, opts),
  }
}

async function toError(response: Response): Promise<ApiClientError> {
  try {
    const parsed: unknown = await response.json()
    if (typeof parsed === 'object' && parsed !== null) {
      const body: Record<string, unknown> = { ...parsed }
      return new ApiClientError(response.status, {
        code: typeof body['code'] === 'string' ? body['code'] : 'unknown',
        message: typeof body['message'] === 'string' ? body['message'] : response.statusText,
        ...(typeof body['detail'] === 'object' && body['detail'] !== null
          ? { detail: { ...body['detail'] } }
          : {}),
      })
    }
  } catch {
    // A gateway timeout or a CloudFront error page is not JSON. Falling through to the status
    // is more useful than a parse error that hides what actually happened.
  }

  return new ApiClientError(response.status, {
    code: 'unknown',
    message:
      response.statusText === '' ? `Request failed (${response.status})` : response.statusText,
  })
}
